import React from 'react'
import PropTypes from 'prop-types'
import ReactDOM from 'react-dom'
import Draggable from 'react-draggable'
import classnames from 'classnames'
import styles from './Tags.icss'
import TagLabel from './TagLabel'
import entityTypes from '../enums/entityTypes'
import Icon from './Icon'

const getPixels = (x, y, width, height) => ({
	x: (width / 100) * x,
	y: (height / 100) * y
})

const getPercentages = (x, y, width, height) => ({
	x: (x / width) * 100,
	y: (y / height) * 100
})

const getPercentageDimensions = (width, height, outerWidth, outerHeight) => ({
	width: (width / outerWidth) * 100,
	height: (height / outerHeight) * 100
})

//	Get the size of an element
const measureElement = (element) => {
	//	eslint-disable-next-line react/no-find-dom-node
	const DOMNode = ReactDOM.findDOMNode(element)
	const nodeStyle = global.getComputedStyle(DOMNode)
	const result = {
		width: DOMNode.offsetWidth,
		height: DOMNode.offsetHeight,
		padding: {
			top: parseInt(nodeStyle.getPropertyValue('padding-top'), 10),
			right: parseInt(nodeStyle.getPropertyValue('padding-right'), 10),
			bottom: parseInt(nodeStyle.getPropertyValue('padding-bottom'), 10),
			left: parseInt(nodeStyle.getPropertyValue('padding-left'), 10)
		}
	}
	return result
}

class Tags extends React.Component {
	constructor(props) {
		super(props)
		this.state = {
			tagDownArrow: {},
			tagsAreHidden: props.hideTagsInitially
		}
		this.tagYOffset = 4
		this.tagXOffset = 0
		this.tagYOffsetIncrement = 8
		this.tagXOffsetIncrement = 25
		this.placedTags = []

		this.isDragging = false
		this.tagLabelElements = []
		this.tagPositions = []
		this.bindClickTag = this.bindClickTag.bind(this)
		this.handleClickContainer = this.handleClickContainer.bind(this)
	}

	componentDidMount() {
		this.setState({
			tagLabelSizes: []
		})
		//	Wait enough time that the tag labels have rendered so we can measure their width/height
		this.sizeInterval = setInterval(() => {
			this.tagLabelElements = this.tagLabelElements.filter(tle => !!tle)
			const prevTagLabelSizes = JSON.stringify(this.state.tagLabelSizes)
			const tagLabelSizes = this.tagLabelElements.map(tle => measureElement(tle))

			if (prevTagLabelSizes !== JSON.stringify(tagLabelSizes)) {
				this.setState({ tagLabelSizes })
			}

			//	Keep track of resized element
			const prevChildSurroundSize = JSON.stringify(this.state.childSurroundSize)
			const childSurroundSize = measureElement(this.childrenSurroundElement)

			if (prevChildSurroundSize !== JSON.stringify(childSurroundSize)) {
				this.setState({ childSurroundSize })
			}

			//	Prevent scrolling via touch move for iOS. Ref: https://stackoverflow.com/a/49853392/6637365
			this.tagLabelElements.forEach((el) => {
				//	eslint-disable-next-line react/no-find-dom-node
				const myEl = ReactDOM.findDOMNode(el)
				if (!myEl.dataset.hastouchmoveeventtopreventiosbug) {
					myEl.addEventListener('touchmove', e => e.preventDefault(), { passive: false })
					myEl.setAttribute('data-hastouchmoveeventtopreventiosbug', true)
				}
			})
		}, 60)
	}

	componentWillUnmount() {
		global.clearInterval(this.sizeInterval)
	}

	getPosition(idx) {
		return this.tagPositions[idx]
	}

	handleClickContainer() {
		this.setState((state) => {
			const result = { tagsAreHidden: this.props.overrideTagsHide(!state.tagsAreHidden) }
			return result
		})
	}

	bindClickTag(index) {
		return () => this.props.clickTag(this.props.tags[index])
	}

	dragEndBinder(tag, index) {
		return (e, data) => {
			this.isDragging = false
			const size = this.state.childSurroundSize
			const coords = getPixels(tag.x, tag.y, size.width, size.height)
			const pcts = getPercentages(coords.x + data.x, coords.y + data.y, size.width, size.height)
			const newTag = Object.assign({}, tag)
			const newTags = this.props.tags.slice()
			delete newTag.autoPlaced
			newTag.x = pcts.x
			newTag.y = pcts.y
			newTag.arrowDown = this.state.tagDownArrow[index]
			newTags[index] = newTag
			this.tagPositions[index] = { x: 0, y: 0 }
			this.props.updateTags(newTags)
		}
	}


	dragBinder(index /* , bounds */) {
		return (e, data) => {
			if (this.tagPositions[index]) {
				// eslint-disable-next-line react/no-access-state-in-setstate
				const tagDownArrow = this.state.tagDownArrow
				const myTagDownArrow = tagDownArrow[index]
				if (this.tagPositions[index].y < data.y) {
					tagDownArrow[index] = false
				}

				if (this.tagPositions[index].y > data.y) {
					tagDownArrow[index] = true
				}
				if (myTagDownArrow !== tagDownArrow[index]) {
					this.setState({ tagDownArrow })
				}
			}

			this.isDragging = true
			this.tagPositions[index] = {
				x: data.x,
				y: data.y
			}
		}
	}

	//	Sets various styles for the tag including position and
	//	transform of the position for correct offset and default position if specified
	styleTag(myTag, index) {
		const tag = myTag
		const size = this.state.childSurroundSize
		const defaultTagPositions = this.props.defaultTagPositions || []
		const elSize = this.state.tagLabelSizes[index]
		const dims = elSize ? getPercentageDimensions(elSize.width, elSize.height, size.width, size.height) : undefined
		if (defaultTagPositions[index]) {
			const dtp = defaultTagPositions[index]
			if (!Number.isFinite(tag.y)) {
				tag.y = dtp.y
			}
			if (!Number.isFinite(tag.x)) {
				tag.x = dtp.x
			}
		} else {
			if ((tag.autoPlaced || !Number.isFinite(tag.y) || tag.y === 0) && this.placedTags.indexOf(tag) === -1) {
				tag.y = this.tagYOffset
				tag.autoPlaced = true
				this.tagYOffset += this.tagYOffsetIncrement
				if (this.tagYOffset > (100 - this.tagYOffsetIncrement)) {
					this.tagYOffset = 0
					this.tagXOffset += this.tagXOffsetIncrement
				}
				this.placedTags.push(tag)
			}
			if (!Number.isFinite(tag.x) || tag.x === 0) {
				//	Set to the left, aligned with the edge
				tag.x = this.tagXOffset + (dims && dims.width ? dims.width / 2 : 0)
			}
		}
		const coords = getPixels(tag.x, tag.y, size.width, size.height)
		const pos = {
			position: 'absolute',
			left: `${!Number.isFinite(coords.x) ? 0 : coords.x}px`,
			top: `${!Number.isFinite(coords.y) ? 0 : coords.y}px`
		}

		if (elSize) {
			pos.transform = `translate(-${elSize.width / 2}px, 0px)`
		} else {
			pos.display = 'none'
		}

		return pos
	}

	render() {
		const tags = this.props.tags ? this.props.tags.slice() : []
		const shouldShowTags = !this.state.tagsAreHidden && !this.props.hideTags && tags.length > 0 && this.state.childSurroundSize && tags && tags.length > 0
		let inVisibleTagCount = 0
		return (
			<div
				className={styles.tagSurround}
				//	eslint-disable-next-line no-return-assign
				ref={r => (this.tagSurroundElement = r)}
				role="button"
				onKeyPress={this.handleClickContainer}
				tabIndex={0}
				onClick={this.handleClickContainer}
			>
				{shouldShowTags && tags.map((tag, idx) => {
					const coords = getPixels(tag.x, tag.y, this.state.childSurroundSize.width, this.state.childSurroundSize.height)
					const isVisible = this.props.isTagVisible ? this.props.isTagVisible(tag) : !tag.hidden
					inVisibleTagCount += !isVisible ? 1 : 0
					const myIndex = idx - inVisibleTagCount
					const bounds = {
						top: -coords.y,
						left: -coords.x + ((this.state.tagLabelSizes[myIndex] ? (this.state.tagLabelSizes[myIndex].width / 2) : 0)),
						right: this.state.childSurroundSize.width - coords.x - ((this.state.tagLabelSizes[myIndex] ? (this.state.tagLabelSizes[myIndex].width / 2) : 0)) - this.state.childSurroundSize.padding.right * 2,
						bottom: this.state.childSurroundSize.height - coords.y - ((this.state.tagLabelSizes[myIndex] ? this.state.tagLabelSizes[myIndex].height : 0)) - this.state.childSurroundSize.padding.bottom * 2
					}

					return (
						<Draggable
							disabled={this.props.readOnly}
							bounds={bounds}
							key={`drag-${tag.text}-${tag.colourName}-${tag.type}-${myIndex}`} 	// eslint-disable-line react/no-array-index-key
							onStop={this.dragEndBinder(tag, myIndex)}
							onDrag={this.dragBinder(myIndex, bounds)}
							position={this.getPosition(myIndex)}
						>
							<div className={classnames(styles.tag, !isVisible ? styles.invisibleTag : undefined)}>
								<TagLabel
									className={styles.fadeTag}
									//	eslint-disable-next-line no-return-assign
									ref={r => (this.tagLabelElements[myIndex] = r)}
									style={this.styleTag(tag, myIndex)}
									arrowDown={typeof this.state.tagDownArrow[myIndex] !== 'undefined' ? this.state.tagDownArrow[myIndex] : tag.arrowDown}
									isUserLabel={tag.type === entityTypes.USER.type}
									onClick={this.bindClickTag(myIndex)}
								>
									{tag.text}
								</TagLabel>
							</div>
						</Draggable>
					)
				})}
				{shouldShowTags && this.props.readOnly && (
					<div className={styles.fadeIcon}>
						<Icon name="whiteBookmark" className={styles.icon} />
					</div>
				)}
				{/*	eslint-disable-next-line no-return-assign */}
				<div ref={r => (this.childrenSurroundElement = r)}>
					{this.props.children}
				</div>
			</div>
		)
	}
}

Tags.propTypes = {
	tags: PropTypes.array,	// eslint-disable-line react/forbid-prop-types
	readOnly: PropTypes.bool,
	updateTags: PropTypes.func,
	clickTag: PropTypes.func,
	defaultTagPositions: PropTypes.array,	// eslint-disable-line react/forbid-prop-types
	children: PropTypes.node.isRequired,
	isTagVisible: PropTypes.func,
	hideTags: PropTypes.bool,
	hideTagsInitially: PropTypes.bool,
	overrideTagsHide: PropTypes.func
}

Tags.defaultProps = {
	tags: undefined,
	readOnly: false,
	isTagVisible: undefined,
	updateTags: () => {},
	clickTag: () => undefined,
	hideTags: false,
	hideTagsInitially: false,
	defaultTagPositions: undefined,
	overrideTagsHide: value => value
}

export default Tags



// WEBPACK FOOTER //
// ./src/components/Tags.jsx